/**
 * Project Sources API — manage data sources (file uploads & connectors) for a project.
 */
import { apiClient, buildUrl } from './apiClient';

export interface ProjectSource {
  source_id: string;
  project_id: string;
  source_type: 'file_upload' | 'connector';
  connector_id: string | null;
  batch_id: string | null;
  label: string | null;
  status: string;
  added_at: string;
  profiled_at: string | null;
  metadata: Record<string, unknown>;
  tenant_id: string | null;
}

export interface AddConnectorSourceRequest {
  connector_id: string;
  tables: string[];
  schema?: string;
  batch_id?: string;
  columns_by_table?: Record<string, string[]>;
}

export async function listProjectSources(projectId: string, batchId?: string): Promise<ProjectSource[]> {
  const params: Record<string, string> = {};
  if (batchId) params.batch_id = batchId;
  const response = await apiClient.get<{ sources: ProjectSource[] }>(
    buildUrl(`/api/v1/projects/${projectId}/sources`),
    { params },
  );
  return response.data?.sources ?? [];
}

export async function addFileSources(
  projectId: string,
  files: File[],
  options: { batchId?: string; columnsByTable?: Record<string, string[]> } = {},
): Promise<ProjectSource> {
  const formData = new FormData();
  files.forEach((file) => formData.append('files', file));
  if (options.batchId) formData.append('batch_id', options.batchId);
  if (options.columnsByTable && Object.keys(options.columnsByTable).length > 0) {
    formData.append('columns_by_table', JSON.stringify(options.columnsByTable));
  }
  const response = await apiClient.post<ProjectSource>(
    buildUrl(`/api/v1/projects/${projectId}/sources`),
    formData,
    { headers: { 'Content-Type': 'multipart/form-data' } },
  );
  return response.data;
}

export async function addConnectorSource(
  projectId: string,
  request: AddConnectorSourceRequest,
): Promise<ProjectSource> {
  const response = await apiClient.post<ProjectSource>(
    buildUrl(`/api/v1/projects/${projectId}/sources`),
    request,
    { headers: { 'Content-Type': 'application/json' } },
  );
  return response.data;
}

export async function removeProjectSource(projectId: string, sourceId: string): Promise<void> {
  await apiClient.delete(buildUrl(`/api/v1/projects/${projectId}/sources/${sourceId}`));
}

export async function triggerRemap(
  projectId: string,
): Promise<{ batch_id: string; status: string; task: string }> {
  const response = await apiClient.post<{ batch_id: string; status: string; task: string }>(
    buildUrl(`/api/v1/projects/${projectId}/remap`),
  );
  return response.data;
}
